// Centralised date/time helpers.
//
// What the backend stores:
//  • Timestamps (shift sessions started_at/ended_at, break_started_at, the
//    decorated start_time/end_time) are absolute moments serialised as UTC ISO
//    strings (trailing "Z").
//  • Calendar values (shift_date, unavailability start_date/end_date) are plain
//    'YYYY-MM-DD' DATEONLY strings with no timezone.
//  • Roster times (shift_start/shift_end) are wall-clock 'HH:MM:SS' strings.
//
// The dashboard is used in many timezones, so every "which day is this" decision
// must use the user's LOCAL calendar day. Using Date.toISOString() for this is a
// bug: it converts to UTC and can shift the day across the midnight boundary
// (e.g. in IST +5:30 or US -5), which is what made "missed clock-in" and shift
// matching behave inconsistently. Always go through these helpers.

// Local 'YYYY-MM-DD' for a Date or any date-ish value.
//  • A UTC timestamp string/Date resolves to the LOCAL calendar day it falls on.
//  • A plain 'YYYY-MM-DD[...]' value with no timezone is returned as-is (its date
//    part) so DATEONLY values are never shifted by being parsed as UTC midnight.


export const localDateKey = (value) => {
    if (value == null) return '';
    if (typeof value === 'string') {
        const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
        const hasTz = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(value);
        if (m && !hasTz) return `${m[1]}-${m[2]}-${m[3]}`;
    }
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Today's local 'YYYY-MM-DD'.
export const todayKey = () => localDateKey(new Date());

// Local 'HH:MM' (24h) for a UTC/ISO timestamp. A plain 'HH:MM[:SS]' wall-clock
// string (no date) is returned as 'HH:MM' unchanged (it carries no timezone).
export const localTime = (value) => {
    if (!value) return null;
    if (typeof value === 'string' && !value.includes('T') && /^\d{1,2}:\d{2}/.test(value)) {
        return value.slice(0, 5);
    }
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
};

// Minutes since local midnight for a timestamp (handy for timeline positioning).
export const localMinutesOfDay = (value) => {
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.getHours() * 60 + d.getMinutes();
};
